import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
} from "@mui/material";
import FullLogo from "../../assets/full-logol.svg";
import AmiTeamImage from "../../assets/images/ami-team.png";
import { loginUrl } from "../../config";
import AuthLayout from "../../layouts/auth/auth_layout";
import { useEffect, useState } from "react";
import { ACCESS_TOKEN } from "../../constants";
import { useNavigate } from "react-router-dom";
import useUser from "../../providers/user/user_provider";
import MerchantLoginDialog from "../merchant/components/merchant_login_dialog/merchant_login_dialog";

const BRAND_PURPLE = "#91278F";
const BRAND_PURPLE_DARK = "#7A2074";
const BRAND_PURPLE_TINT = "#F5E6F5";


export default function Auth() {
  const navigate = useNavigate();
  let { userPermissions, loading } = useUser()
  let token = sessionStorage.getItem(ACCESS_TOKEN);
  const [merchantLoginOpen, setMerchantLoginOpen] = useState(false);

  useEffect(() => {
    if (token) {
      // Check if it's a merchant login first
      const merchantData = sessionStorage.getItem('merchant_data');
      if (merchantData) {
        navigate('/merchant');
        return;
      }

      // For admin users, wait for permissions to load
      if (!loading && userPermissions) {
        let module = userPermissions?.permissions?.[0]?.module;
        
        if (userPermissions?.permissions?.[0]?.module === "choices") {
          module = "surveys";
        }

        const nav = `/${module}`;
        // Reports is Platform Admin only — not even a tenant's own Super
        // Admin (same gate as the "Reports" nav item in use_nav_options.tsx).
        // superAdmin is a tenant-scoped role flag, not Platform-org
        // membership, so using it here sent any tenant Super Admin to a
        // page their own sidebar doesn't even show them.
        navigate(userPermissions?.isPlatformOrg ? "/reports" : nav);
      }
    }
  }, [navigate, userPermissions, token, loading]);

  const handleMerchantLogin = () => {
    setMerchantLoginOpen(true);
  };

  const handleMerchantLoginSuccess = (merchantData: any) => {
    // Navigation will be handled by the useEffect when permissions are loaded
    // No need to navigate manually here
  };
  return (
    <AuthLayout>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 3 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 900,
            borderRadius: 4,
            overflow: "hidden",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            boxShadow: "0 24px 60px -20px rgba(145, 39, 143, 0.35)",
          }}
        >
          {/* Photo panel */}
          <Box
            sx={{
              position: "relative",
              width: { xs: "100%", md: "44%" },
              height: { xs: 180, sm: 240, md: "auto" },
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={AmiTeamImage}
              alt="The AMI team"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 30%",
                display: "block",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(37,10,36,0) 45%, rgba(37,10,36,0.72) 100%)",
              }}
            />
          </Box>

          {/* Form panel */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "white",
              px: { xs: 3, sm: 5 },
              py: { xs: 4, sm: 6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Box
              component="img"
              src={FullLogo}
              alt="AMI logo"
              sx={{ width: 300, maxWidth: "100%", display: "block", mb: 2.5 }}
            />
            <Typography variant="h5" fontWeight={700} color="#2A0F29">
              Welcome
            </Typography>
            <Typography
              variant="body2"
              color="#767676"
              sx={{ maxWidth: 320, mt: 1, mb: 3 }}
            >
              To access the admin portal, please log in using one of the
              options below.
            </Typography>

            <Stack spacing={1.5} width="100%" maxWidth={320}>
              <Button
                href={loginUrl}
                variant="contained"
                size="large"
                disableElevation
                sx={{
                  bgcolor: BRAND_PURPLE,
                  "&:hover": { bgcolor: BRAND_PURPLE_DARK },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.25,
                }}
              >
                Log in as admin
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleMerchantLogin}
                sx={{
                  borderColor: BRAND_PURPLE,
                  color: BRAND_PURPLE,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.25,
                  "&:hover": {
                    borderColor: BRAND_PURPLE,
                    bgcolor: BRAND_PURPLE_TINT,
                  },
                }}
              >
                Log in as merchant
              </Button>
            </Stack>

            <Typography variant="caption" color="#9E9E9E" sx={{ mt: 4 }}>
              © {new Date().getFullYear()} AMI. All rights reserved.
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* Merchant Login Dialog */}
      <MerchantLoginDialog
        open={merchantLoginOpen}
        onClose={() => setMerchantLoginOpen(false)}
        onSuccess={handleMerchantLoginSuccess}
      />
    </AuthLayout>
  );
}

